import { type UIViewInjectedProps, UIView } from '@uirouter/react';
import { Descriptions, Tabs } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { Drawer } from '@/components/drawer';
import { cooperativeStore } from '@/stores';
import { booleanFormatted } from '@/utils/common';
import { getTranslatedString } from '@/utils';

import { CooperativeLegalAttachments, CooperativePolygonAttachments } from '../attachments';

const { TabPane } = Tabs;

export const CooperativeView = observer(({ transition }: UIViewInjectedProps) => {
  const cooperativeId = +transition.router.globals.params?.cooperativeId;
  const cooperative = cooperativeStore.items.find(
    (item: cyrian.api.cooperatives.Cooperative) => item.id === cooperativeId,
  );

  useEffect(() => {
    if (cooperativeId && !cooperative) {
      api.cooperatives
        .get(cooperativeId)
        .source.then(({ data }) => cooperativeStore.addItem(data))
        .catch(() => {});
    }
  }, [cooperativeId, cooperative]);

  const onClose = () => {
    transition.router.stateService.go('base-layout.cooperatives');
  };

  const title = cooperative
    ? getTranslatedString('cooperatives.cooperative-number', { 0: cooperative.id })
    : `Cooperative #${cooperativeId}`;

  const item = cooperative ?? ({} as cyrian.api.cooperatives.Cooperative);

  return (
    <>
      <Drawer onClose={onClose} visible={true} title={title}>
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label={<FormattedMessage id="counterparties.name" />}>{item.name}</Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="counterparties.code" />}>
            {item.code ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="cooperative.isEudrCompliant" />}>
            {item.isEudrCompliant != null ? booleanFormatted(item.isEudrCompliant) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="counterparties.consortium" />}>
            {item.consortiums?.length ? item.consortiums.map((c) => c.name).join(', ') : '—'}
          </Descriptions.Item>
        </Descriptions>
        <Tabs defaultActiveKey="polygons" style={{ textAlign: 'center' }}>
          <TabPane tab="Polygons" key="polygons">
            <CooperativePolygonAttachments transition={transition} />
          </TabPane>
          <TabPane tab="Legals" key="legals">
            <CooperativeLegalAttachments transition={transition} />
          </TabPane>
        </Tabs>
      </Drawer>
      <UIView />
    </>
  );
});
